/**
 * CSK Design System — brand tokens.
 *
 * Single source of truth for all CSK colors, typography, and theorem
 * color assignments. Derived from the design spec in DemoKit.html.
 *
 * Import directly to avoid bundle bloat — no CSS-in-JS runtime required.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type {
  CompositionalityPrincipleName,
  DualPairGuaranteeName,
  GeneratorCode,
  NeutralizationTheoremName,
} from "@csk/core";
import tokenData from "./tokens.data.json" with { type: "json" };

// ── Color palette ──────────────────────────────────────────────

type NumericKeyMap<T extends Record<string, string>> = {
  [K in keyof T as K extends `${infer N extends number}` ? N : never]: T[K];
};

function toScale<T extends Record<string, string>>(entries: T): Readonly<NumericKeyMap<T>> {
  return Object.fromEntries(
    Object.entries(entries).map(([key, value]) => [Number(key), value]),
  ) as NumericKeyMap<T>;
}

/** Primary blue — trust, security, authority. */
export const PRIMARY = toScale(tokenData.primary);

/** Safe green — verified, protected, compliant. */
export const SAFE = toScale(tokenData.safe);

/** Warning red — vulnerable, attack, non-compliant. */
export const WARN = toScale(tokenData.warn);

/** Neutral grays. */
export const GRAY = toScale(tokenData.gray);

type ModeKey = keyof typeof tokenData.modes;

export const MODE_COLOR: Readonly<
  Record<ModeKey, { primary: string; dim: string; label: string }>
> = tokenData.modes;

export const GENERATOR_COLOR: Readonly<Record<GeneratorCode, { primary: string; label: string }>> =
  tokenData.generators;

export const DEMO_COLOR = tokenData.semantic;

export type DemoColorKey = keyof typeof DEMO_COLOR;

export const DUAL_PAIR_COLOR: Readonly<
  Record<DualPairGuaranteeName, { color: string; bg: string }>
> = Object.fromEntries(
  Object.entries(tokenData.dualPairs).map(([name, def]) => [
    name,
    { color: def.color, bg: def.bg },
  ]),
) as Record<DualPairGuaranteeName, { color: string; bg: string }>;

export const COMPOSITIONALITY_PRINCIPLE_COLOR: Readonly<
  Record<CompositionalityPrincipleName, { color: string; bg: string; level: 1 | 2 }>
> = Object.fromEntries(
  Object.entries(tokenData.compositionalityPrinciples).map(([name, def]) => [
    name,
    { color: def.color, bg: def.bg, level: def.level },
  ]),
) as Record<CompositionalityPrincipleName, { color: string; bg: string; level: 1 | 2 }>;

// ── Theorem color assignments ──────────────────────────────────

/**
 * Per-theorem accent colors keyed by canonical theorem name.
 * Colors mirror the visual-identity registry's theorem namespace directly.
 */
export type CanonicalTheoremName = NeutralizationTheoremName;

export const THEOREM_COLOR: Readonly<Record<CanonicalTheoremName, string>> = Object.fromEntries(
  Object.entries(tokenData.theorems).map(([name, def]) => [name, def.color]),
) as Record<CanonicalTheoremName, string>;

export const THEOREM_CODE: Readonly<Record<CanonicalTheoremName, string>> = Object.fromEntries(
  Object.entries(tokenData.theorems).map(([name, def]) => [name, def.code]),
) as Record<CanonicalTheoremName, string>;

/**
 * Per-theorem background washes (low-alpha tints of the theorem color).
 * Sourced directly from `tokens.data.json`'s `bg` field so backgrounds
 * stay tied to the theorem hue across all consumers.
 */
export const THEOREM_BG: Readonly<Record<CanonicalTheoremName, string>> = Object.fromEntries(
  Object.entries(tokenData.theorems).map(([name, def]) => [name, def.bg]),
) as Record<CanonicalTheoremName, string>;

// ── Color helpers ──────────────────────────────────────────────

export function canonicalTheoremColor(
  name: string,
  fallback: string = DEMO_COLOR.mutedSteel,
): string {
  if (Object.hasOwn(THEOREM_COLOR, name)) {
    return THEOREM_COLOR[name as CanonicalTheoremName];
  }
  if (Object.hasOwn(DUAL_PAIR_COLOR, name)) {
    return DUAL_PAIR_COLOR[name as DualPairGuaranteeName].color;
  }
  if (Object.hasOwn(COMPOSITIONALITY_PRINCIPLE_COLOR, name)) {
    return COMPOSITIONALITY_PRINCIPLE_COLOR[name as CompositionalityPrincipleName].color;
  }
  return fallback;
}

export function canonicalGeneratorColor(
  code: string,
  fallback: string = DEMO_COLOR.mutedSteel,
): string {
  return Object.hasOwn(GENERATOR_COLOR, code)
    ? GENERATOR_COLOR[code as GeneratorCode].primary
    : fallback;
}

export function resolveDemoColor(
  key: string | undefined,
  fallback: string = DEMO_COLOR.sky,
): string {
  if (!key) return fallback;
  if (key.startsWith("#")) return key;
  return Object.hasOwn(DEMO_COLOR, key) ? DEMO_COLOR[key as DemoColorKey] : fallback;
}

/**
 * Append an opacity byte to a 6-digit hex color.
 *
 * @param hex - A 6-digit (`#RRGGBB`) hex color.
 * @param alphaHex - A 2-digit hex opacity byte (e.g. `"1A"` ≈ 10%, `"33"` ≈ 20%).
 *   Pass with no leading "#"; only the hex digits are appended verbatim.
 *
 * Used by components that need to derive a low-alpha background wash from
 * a theorem or generator color without introducing a runtime CSS-in-JS
 * dependency. For 3-digit hex inputs or named colors, fall back to inline
 * `rgba()` literals.
 */
export function withAlpha(hex: string, alphaHex: string): string {
  return `${hex}${alphaHex}`;
}

// ── Typography ─────────────────────────────────────────────────

export type FontRole = keyof typeof tokenData.font;
export const FONT: Readonly<Record<FontRole, string>> = tokenData.font;

// ── Status → style mapping ─────────────────────────────────────

/**
 * Maps a demo result type to its display colors.
 * "success" means the attack succeeded (bad outcome for the user).
 * "neutralized" means CSK made the violation unconstructable by construction.
 */
export const RESULT_STYLE: Readonly<
  Record<"success" | "neutralized" | "info", { bg: string; border: string; titleColor: string }>
> = {
  success: { bg: WARN[100], border: WARN[300], titleColor: WARN[700] },
  neutralized: { bg: SAFE[100], border: SAFE[300], titleColor: SAFE[700] },
  info: { bg: PRIMARY[100], border: PRIMARY[300], titleColor: PRIMARY[700] },
} as const;
